// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, RadioTower, BarChart3, Settings2, MessageCircle, Code2 } from 'lucide-react';
import ChatbotChannelsTab from '../components/marketplace/ChatbotChannelsTab';
import ChatbotStatsTab from '../components/marketplace/ChatbotStatsTab';
import ChatbotCustomizeTab from '../components/marketplace/ChatbotCustomizeTab';
import DeveloperTab from '../components/marketplace/developer/DeveloperTab';
import { getClientProfile } from '../services/api';

// The page is the AI Assistant: one assistant, answering on every channel it is
// connected to. The address stays /marketplace/chatbot because the Marketplace
// card, its prepare step, Orbit and the old-route redirects all point there.
type TabType = 'channels' | 'stats' | 'customize' | 'developer';

const tabs: { id: TabType; label: string; icon: React.ElementType }[] = [
  { id: 'channels', label: 'Channels', icon: RadioTower },
  { id: 'stats', label: 'Stats', icon: BarChart3 },
  { id: 'customize', label: 'Customize', icon: Settings2 },
  { id: 'developer', label: 'Developer', icon: Code2 },
];

// Links written before Channels replaced the Integration tab still say #integration.
const tabFromHash = (hash: string): TabType | null => {
  const id = hash.replace('#', '');
  if (id === 'integration') return 'channels';
  return tabs.some((t) => t.id === id) ? (id as TabType) : null;
};

const ChatbotDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const clientId = localStorage.getItem('client_id') || localStorage.getItem('impersonated_client_id') || '';
  const [activeTab, setActiveTab] = useState<TabType>(() => tabFromHash(location.hash) || 'channels');
  const [profile, setProfile] = useState<any>(null);

  // Sync tab with URL hash
  useEffect(() => {
    const tab = tabFromHash(location.hash);
    if (tab) setActiveTab(tab);
  }, [location.hash]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (clientId) {
          const data = await getClientProfile(clientId);
          setProfile(data);
        }
      } catch (error) {
        console.error('Failed to load profile:', error);
      }
    };
    fetchProfile();
  }, [clientId]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    navigate(`/marketplace/chatbot#${tab}`, { replace: true });
  };

  const brandColor = profile?.branding?.primary_color || '#4F46E5';

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/marketplace')}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${brandColor}15` }}
          >
            <MessageCircle size={20} style={{ color: brandColor }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">AI Assistant</h1>
            <p className="text-sm text-gray-500">Trained on your business data. Answers on your website and WhatsApp.</p>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-1 overflow-x-auto" aria-label="AI Assistant tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors
                  ${isActive
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'channels' && <ChatbotChannelsTab clientId={clientId} profile={profile} />}
        {activeTab === 'stats' && <ChatbotStatsTab clientId={clientId} />}
        {activeTab === 'customize' && <ChatbotCustomizeTab clientId={clientId} />}
        {activeTab === 'developer' && <DeveloperTab clientId={clientId} />}
      </div>
    </div>
  );
};

export default ChatbotDetailPage;
